import React, { useEffect, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Text from '@amzn/meridian/text';
import AppliedFilterTags from 'src/components/shared/appliedFilterTags/appliedFilterTags';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import NewFeatureBanner from 'src/components/shared/newFeatureBanner/newFeatureBanner';
import PaginationWithDropdown from 'src/components/shared/paginationWithDropdown/paginationWithDropdown';
import ToggleFilterButton from 'src/components/shared/toggleFilterButton/toggleFilterButton';
import { JOB_MODIFICATIONS_WIKI_LINK, RTW_WIKI_URL, T2_JOB_REFERENCE_CARD } from 'src/config/ExternalLinks';
import {
  applyFilters,
  getOpenAndClosedPlacementsBySiteId,
  removeTagAndUpdateFilters,
  setCurrentPage,
  setIsAdvancedFilterSheetOpen,
  setPerPage,
  setSearchField,
  setSelectedFilterOptions,
} from 'src/store/placementOverviewPageSlice';
import { setIsSiteSelectionDisabled, setSelectedSite } from 'src/store/siteSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { setPreferredSite } from 'src/store/userSlice';

import './placementsOverview.scss';

import AdvancedFilterSheet from './advancedFilterSheet/advancedFilterSheet';
import PlacementOverviewTable from './placementOverviewTable/placementOverviewTable';
import TabGroup from './tabGroup/tabGroup';

const isTabValid = (tab: string) =>
  [
    'pendingPlacements',
    'activePlacements',
    'expiredPlacements',
    'closedPlacements',
    'transferredInPlacements',
  ].includes(tab);

/**
 * Default component.
 */
const PlacementsOverview = () => {
  const dispatch = useAppDispatch();
  const { tab, site } = useParams();
  const {
    searchField,
    appliedFilterOptions,
    selectedFilterOptions,
    availableFilterOptions,
    currentPage,
    totalPages,
    perPage,
    filterTags,
  } = useAppSelector((state) => state.placementOverviewPage);
  const navigate = useNavigate();
  const { userLoading } = useAppSelector((state) => state.user);
  const { selectedSite, allSites, allowedSites } = useAppSelector((state) => state.sites);
  const { featureFlags } = useAppSelector((state) => state.user);
  const [currentTab, setCurrentTab] = useState(tab || 'pendingPlacements');
  const siteRef = useRef<string | undefined>(site);

  // Logic to do when site updates
  useEffect(() => {
    if (userLoading === 'fulfilled' && selectedSite.site) {
      if (selectedSite.site && selectedSite.site !== siteRef.current) {
        siteRef.current = selectedSite.site;
        navigate(`/placementsOverview/${siteRef.current}/pendingPlacements`);
      }
      if (!allowedSites.map((allowedSite) => allowedSite.site).includes(siteRef.current!)) {
        navigate('unauthorized');
      }
      dispatch(getOpenAndClosedPlacementsBySiteId(selectedSite.id));
    }
    return () => {
      dispatch(setIsSiteSelectionDisabled(false));
    };
  }, [userLoading, selectedSite]);

  // Set site object
  useEffect(() => {
    if (siteRef && allSites.length > 0) {
      const siteObj = allSites.filter((s) => s.site === siteRef.current)[0];
      if (siteObj) {
        dispatch(setSelectedSite(siteObj));
        dispatch(setPreferredSite(siteObj.id));
        if (tab && currentTab !== tab && isTabValid(tab)) {
          navigate(`/placementsOverview/${siteObj.site}/${currentTab}`);
          dispatch(setCurrentPage(1));
        }
        if (tab && !isTabValid(tab)) {
          setCurrentTab('pendingPlacements');
          navigate(`/placementsOverview/${siteObj.site}/pendingPlacements`);
          dispatch(setCurrentPage(1));
        }
      }
    }
  }, [tab, siteRef, allSites, currentTab, dispatch, navigate]);

  return (
    <Column height="100%" spacingInset="450">
      <Column spacingInset="none none 400 none">
        {/* -- Alerts -- */}
        <Alert type="theme" aria-label="UX Improvements">
          <Text>
            Updates will be made to the Placement Details UX over the course of April and May.&nbsp;
            <Link href={RTW_WIKI_URL} target="_blank" rel="noopener noreferrer">
              Learn more on our RTW Wiki
            </Link>
          </Text>
        </Alert>
        {featureFlags.JOB_MODIFICATIONS && selectedSite.jobModificationsEnabled && (
          <NewFeatureBanner
            label="jobModificationsFeatureAlert"
            text={
              <>
                Pre-approved job modifications are now available in the RTW tool to support efficient placement
                decisions.
              </>
            }
            link={JOB_MODIFICATIONS_WIKI_LINK}
          />
        )}
        {featureFlags.T2_PLUS_EXPERIENCE && (
          <NewFeatureBanner
            label="t2ExperienceFeatureAlert"
            text={
              <>
                The RTW tool now supports accommodation placement entry for associates at all job levels. Questions on
                Return to Work's T2+ experience? Please refer to our
              </>
            }
            link={T2_JOB_REFERENCE_CARD}
            linkText="Quick Reference Card."
          />
        )}
        <NewFeatureBanner
          label="twpaPdfUpdate"
          text="Updates have been made to the temporary work placement acknowledgement letter."
        />

        {/* -- Main Content -- */}
        {availableFilterOptions.availablePlacementTypes.length ? (
          <>
            <Row alignmentHorizontal="justify">
              <Heading level={1}>{`Placements Overview for ${selectedSite.site}`}</Heading>
              <Button type="primary" onClick={() => navigate('/newJMR')}>
                Create Placement
              </Button>
            </Row>
            <TabGroup currentTab={currentTab} setCurrentTab={setCurrentTab} />
            <Box type="fill" spacingInset="300">
              <Row widths={['grid-3', 'fit', 'fit']}>
                <SearchField
                  value={searchField}
                  onChange={(newSearchField) => dispatch(setSearchField(newSearchField))}
                  placeholder="Search for..."
                  onSubmit={() => {}}
                />

                <ButtonGroup
                  value={selectedFilterOptions.placementTypes}
                  onChange={(newValue) => {
                    dispatch(setSelectedFilterOptions({ ...selectedFilterOptions, placementTypes: newValue }));
                    dispatch(applyFilters());
                  }}
                >
                  {availableFilterOptions.availablePlacementTypes.map((placementType: string) => (
                    <ButtonOption key={placementType} value={placementType}>
                      {placementType}
                    </ButtonOption>
                  ))}
                </ButtonGroup>

                {currentTab !== 'transferredInPlacements' && (
                  <ToggleFilterButton
                    data-testid="placements-overview__filter-icon"
                    tooltipTitle="Open Placements Overview Filter"
                    onClick={() => {
                      dispatch(setIsAdvancedFilterSheetOpen(true));
                      dispatch(setSelectedFilterOptions(appliedFilterOptions));
                    }}
                  />
                )}
              </Row>
            </Box>
            <AppliedFilterTags
              filterTags={filterTags}
              onDismissTag={(tag) => dispatch(removeTagAndUpdateFilters(tag))}
            />
            <PlacementOverviewTable currentTab={currentTab} />
            <AdvancedFilterSheet />
            <PaginationWithDropdown
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              setPerPage={(newPerPage) => dispatch(setPerPage(newPerPage))}
              setCurrentPage={(newPage) => dispatch(setCurrentPage(newPage))}
            />{' '}
          </>
        ) : (
          <LoadingSpinner />
        )}
      </Column>
    </Column>
  );
};

export default PlacementsOverview;
